'use strict';

/*
 * NOTE: These TermUtils were copied directly from lib-react
 */

const moment = require('moment');
const { size, isFalse, isEmpty } = require('./Utils');

/**
 * Utility methods for Term related stuff.
 */
const TermUtils = {};

TermUtils.SIZE = {
    SHORT: "SHORT",
    MEDIUM: "MEDIUM",
    LONG: "LONG"
};

TermUtils.TERMS = {
    1: "Winter",
    2: "Spring",
    3: "Summer",
    4: "Autumn"
};

/**
 * Checks a termId to make sure it is in the proper format (e.g. "20191" or "20193A")
 * A proper format is 4 digits, followed by a digit between 1 and the size of the TermUtils.TERMS, and optional followed by an A or B for subterms.
 * If the subterms standard changes, this regex will need to be updated to reflect those changes.
 *
 * @param termId An atpId (e.g. "20182")
 * @returns A boolean if the termId provided passes the format test
 */
TermUtils.isValidFormattedTermId = function(termId) {
    // Make sure the termId comes as a string
    termId = String(termId);

    // Regex for the termId being a 5 or 6 character string that starts with 4 numbers (for year), followed by a number 1 through size of terms object (for term), and possibly ending with an A or B
    let termIdRegex = "(^[0-9]{4}[1-" + size(TermUtils.TERMS) + "]$)|(^[0-9]{4}[1-" + size(TermUtils.TERMS) + "][abAB]$)";
    let regex = new RegExp(termIdRegex,"g");
    // return boolean if the termId matches the regex
    return regex.test(termId);
};

/**
 * Makes a term name from a termId
 *
 * @param termId An atpId (e.g. "20182")
 * @returns A string of the term name from a termId (e.g. "Spring")
 */
TermUtils.makeTermName = function(termId, size) {
    // Check that the termId provide is valid, if not return null
    if ( isFalse(TermUtils.isValidFormattedTermId(termId)) ) {
        return null;
    }

    // convert the termId to a term object
    let termObj = TermUtils._makeTermObject(termId);

    if (size === this.SIZE.SHORT) {
        return termObj.termShortName;
    }

    return termObj.termName;
};


/**
 * Makes a term description (e.g. "Winter 2016" or "WI 16") from a term Id.
 *
 * @param termId An atpId (e.g. "20181")
 * @param size A size for the term description (e.g. "LONG" or "SHORT"). Optional, defaults to LONG.
 * @returns A string of a term description
 */
TermUtils.makeTermDescriptionFromTermId = function(termId, size) {
    // Check that the termId provide is valid, if not return null
    if ( isFalse(TermUtils.isValidFormattedTermId(termId)) ) {
        return null;
    }

    if (size === this.SIZE.SHORT) {
       return(TermUtils._getTermShortName(TermUtils._makeTermDescription(termId)));
    }
    return (TermUtils._makeTermDescription(termId));
};

/**
 * Makes a term link identifier (e.g. "su20") from a term Id.
 * @param termId An atpId (e.g. "20203")
 * @return A string of a term identifier
 */
TermUtils.makeTermLinkIdentifierFromTermId = function(termId) {
    return ( TermUtils._getTermShortName( TermUtils._makeTermDescription(termId) ).toLowerCase().replace(/\s+/g, '') );
};

/**
 * Makes a termId, or atpId (e.g. "20174") from a term description.
 *
 * @param termName A string of a term description (e.g. "Winter 2017", "SP 17", "Summer B 2017")
 * @returns A termId string if one can be matched. Otherwise, null.
 */
TermUtils.makeTermIdFromTermName = function(termName) {
    let year = TermUtils._parseYear(termName);
    if (! year) {
        return null;
    }

    // This is a pretty loose match, but is probably sufficient.
    let termNumber;
    if (termName.toUpperCase().match(/WI/)) {
        termNumber = 1;
    } else if (termName.toUpperCase().match(/SP/)) {
        termNumber = 2;
    } else if (termName.toUpperCase().match(/SU/)) {
        termNumber = 3;
    } else if (termName.toUpperCase().match(/AU/)) {
        termNumber = 4;
    } else {
        return null;
    }

    // Only assuming that there is a space around the A or B for sub-terms.
    let termSuffix = "";
    if (termName.toUpperCase().match(/ A /)) {
        termSuffix = "A";
    } else if (termName.toUpperCase().match(/ B /)) {
        termSuffix = "B";
    }

    return (`${year}${termNumber}${termSuffix}`);
};

/**
 * Returns the parse term object for the given term id, if it can be built.
 *
 * @param termId ther term id
 * @returns an object with fields parsed from the term id, or null if the term id is not valid
 */
TermUtils.buildTermObject = function(termId) {
    // Check that the termId provide is valid, if not return null
    if ( isFalse(TermUtils.isValidFormattedTermId(termId)) ) {
        return null;
    }

    // convert the termId to a term object
    let termObj = TermUtils._makeTermObject(termId);

    return termObj;
};

/**
 * Discovers the academic calendar entry in the given term service object for the
 * period one priority registration start date, and returns that date as a parsed moment object.
 *
 * If the entry cannot be found in the expected mapping, or the expected format, null is returned
 *
 * @param termInfo the term data object from the term service
 */
TermUtils.findPeriodOnePriorityRegDate = function(termInfo) {

    let result = null;

    if (!isEmpty(termInfo)) {
        let calendarEntries = termInfo.academicCalendar;

        if (!isEmpty(calendarEntries)) {
            calendarEntries.forEach( entry => {
                if (!isEmpty(entry.key) && entry.key === 'FirstRegistrationPeriod') {

                    let format = "YYYY-MM-DD";
                    // check to make sure that a date with extra characters beyond the format are invalid and return null,
                    // rather that silently ignored by moment
                    if (moment(entry.date, "YYYY-MM-DD", true).isValid()) {
                        result = moment(entry.date, "YYYY-MM-DD", true);
                    }
                }
            });
        }
    }

    return result;
};

/**
 * Discovers the academic calendar entry in the given term service object for the
 * period one priority registration start date, and returns that date as a parsed moment object.
 *
 * If the entry cannot be found in the expected mapping, or the expected format, null is returned
 *
 * @param termInfo the term data object from the term service
 * @param calendarKey the key to the academic calendar entry
 */
TermUtils.findAcademicCalendarDate = function(termInfo, calendarKey) {
    let result = null;

    if (!isEmpty(termInfo)) {
        let calendarEntries = termInfo.academicCalendar;

        if (!isEmpty(calendarEntries)) {
            let format = "YYYY-MM-DD";
            let targetEntry = calendarEntries.find(entry => !isEmpty(entry.key) && entry.hasOwnProperty("key") && entry.key === calendarKey);

            // check to make sure that a date with extra characters beyond the format are invalid and return null,
            // rather that silently ignored by moment
            if (targetEntry && targetEntry.hasOwnProperty("date") && moment(targetEntry.date, "YYYY-MM-DD", true).isValid()) {
                result = moment(targetEntry.date, "YYYY-MM-DD", true);
            }
        }
    }

    return result;
};



/**
 * Generates a string which represents the academic year spans.
 * There are 3 formats - short: "yyYY"; medium: "yyyy-YY"; long: "yyyy-YYYY"
 * Academic Year 2018-2019 means the code returned should be "1819", "2018-19", or "2018-2019". Academic year span is determined by the year in the termId.
 * Academic year starts in Autumn (which is term number 4). Not sure why, but I made it work for the turn of the century.
 *
 * @param termId The term for which an academic year combination should be generated (e.g. "20193")
 * @param size A size for the term description (e.g. "LONG" or "MEDIUM" or "SHORT"). Optional, defaults to SHORT.
 * @return {string} An academic year code in one of the 3 formats
 */
TermUtils.getAcademicYearCode = function(termId, size = this.SIZE.SHORT) {
    // Check that the termId provide is valid, if not return null
    if ( isFalse(TermUtils.isValidFormattedTermId(termId)) ) {
        return null;
    }

    let code = [];
    let tempCalcYear;
    // First 2 digits of the term year
    let century = termId.substring(0, 2);

    let termNumber = termId.substring(4, 5); // Get the term number from the 5th character
    // Get the last 2 digits from the year, 3rd and 4th characters from the string
    let termYear = termId.substring(2, 4);

    // Academic year starts in Autumn term (aka termNumber = 4)
    if (parseInt(termNumber) === 4) {
        // If termNumber is 4 (Autumn), first 2 numbers in the code are the year of term, last 2 are the term year + 1 (e.g. 20194 = 1920)
        code.push(termYear);

        tempCalcYear = (parseInt(termYear) + 1).toString();

        // Handle years that need a leading 0 or to be 00
        if ( tempCalcYear.length !== 2) {
            // If 1 character, add a leading 0, if 3 characters, use last 2 characters
            tempCalcYear = tempCalcYear.length === 1 ? "0" + tempCalcYear : tempCalcYear.slice(-2);
        }

        code.push(tempCalcYear);
    } else {
        // If termNumber is not 4, the first 2 numbers in the code are the year of the term - 1, last 2 are the year of the term (e.g. 20193 = 1819)
        if (termYear === "00") {
            tempCalcYear = "99";
            // If year ends on 00, then the first year of the academic year is previous century
            century = century - 1;
        } else {
            tempCalcYear = (parseInt(termYear) - 1).toString();
        }

        // Handle years that need a leading 0 or to be 00
        if ( tempCalcYear.length !== 2) {
            // If 1 character, add a leading 0, if 3 characters, use last 2 characters
            tempCalcYear = tempCalcYear.length === 1 ? "0" + tempCalcYear : tempCalcYear.slice(-2);
        }

        code.push(tempCalcYear);
        code.push(termYear);
    }

    return TermUtils._formatAcademicYearCode(century, code.join(''), size);
};

/**
 * Generates a string representation of the format 'WIN 2020' for a given termId 20201
 * @param termId The term for which the quarter year string needs to be generated
 * @returns {string}
 */
TermUtils.makeQtrYearFromTermId = function(termId) {
    let termObj = TermUtils.buildTermObject(termId);
    let termString = "";
    if (termObj) {
        termString = termObj.termQtrName + " " +termObj.termYear;
    }
    return (termString);
};

/**
 * Reformats a given academic year code to one of 3 sizes.
 *
 * For example, the academic year of 2013-2014 could be reformatted to "1314", "2013-14", "2013-2014"
 * The given code to reformat should always be in the short format.
 *
 * @param century First 2 digits of the year
 * @param yearCode The academic year to be re-formatted. This should be in the short format (i.e. '1314' for the academic year 2013-2014)
 * @param size A size for the term description (e.g. "SHORT" or "MEDIUM" or "LONG"). Optional, defaults to SHORT.
 * @return {string} An academic year code in one of these formats: "yyYY", "yyyy-YY", "yyyy-YYYY"
 */
TermUtils._formatAcademicYearCode = function(century, yearCode, size = TermUtils.SIZE.SHORT) {
    let year1 = yearCode.substring(0, 2);
    let year2 = yearCode.substring(2, 4);

    let century1 = century, century2 = century;

    if ( year2 === "00" ) {
        century2 = parseInt(century) + 1;
    }

    if (size === TermUtils.SIZE.LONG) {
        return `${century1}${year1}-${century2}${year2}`;
    } else if (size === TermUtils.SIZE.MEDIUM) {
        return `${century1}${year1}-${year2}`;
    } else {
        return yearCode;
    }
}


/**
 * Attempts to turn a bit of text into a year of format YYYY. (This method won't work correctly in the 22nd century).
 *
 * @param text A string with a year embedded in it (e.g. "Summer B 2017" or "AU 17")
 * @return A year string if one can be found. Otherwise, null.
 * @private
 */
TermUtils._parseYear = function(text) {
    let year = text.replace(/[^\d+]/g, '');

    if (year.length === 2) {
        year = "20" + year;
    } else if (year.length !== 4) {
        year = null;
    }

    return year;
};


/**
 * Attempts to create a term object from a termId or atpId
 *
 * @param termId An aptId (e.g. "20184")
 * @returns An object of term data including year, name, number, and subterm if the termId can be matched. Otherwise, null.
 * @private
 */
TermUtils._makeTermObject = function(termId) {
    // Check that the termId provide is valid, if not return null
    if ( isFalse(TermUtils.isValidFormattedTermId(termId)) ) {
        return null;
    }

    let termNumber = termId.substring(4, 5); // Get the term number from the 5th character
    let termYear = termId.substring(0, 4); // Get the year from first 4 characters
    let subtermId = (termId.length === 6 ? termId.substring(5, 6).toUpperCase() : null); // Get the subterm from the 6th character if string length is 6

    let termObj = {
        "termYear": termYear,
        "termNumber": termNumber,
        "termName": TermUtils.TERMS[termNumber],
        "termShortName": TermUtils.TERMS[termNumber].substring(0, 2).toLowerCase(),
        "termQtrName": TermUtils.TERMS[termNumber].substring(0, 3).toUpperCase(), //e.g WIN
        "subtermName": subtermId
    };

    // Make sure the term object has valid data, if not return null
    if ( !termObj.termName || termObj.termNumber === 0 || termObj.termNumber > size(TermUtils.TERMS) ) {
        return null;
    }

    return termObj;
};

/**
 * Creates a short text version of a term description (e.g. "Winter 2018" into "WI 18")
 *
 * @param term A string of a term description (e.g. "Winter 2018", "Winter")
 * @returns A string of a shortened term description (e.g. "WI 18")
 * @private
 */
TermUtils._getTermShortName = function(term) {
    let temp = term.split(" ");
    let termName, termYear;
    if (temp.length === 1) {
        return temp[0].substring(0, 2).toUpperCase();
    } else if (temp.length > 2) {
        termName = temp[0].substring(0, 2).toUpperCase() + " " + temp[1].toUpperCase();
        termYear = temp[2].slice(-2);
    } else {
        termName = temp[0].substring(0, 2).toUpperCase();
        termYear = temp[1].slice(-2);
    }

    return termName + " " + termYear;
};


/**
 * Makes a term description from a term id.
 *
 * @param termId An atpId (e.g. "20164").
 * @returns A term description (e.g. "Winter 2016") if the termId can be matched. Otherwise, null.
 * @private
 */
TermUtils._makeTermDescription = function(termId) {
    let termObj = TermUtils._makeTermObject(termId);

    if (!termObj) return null;

    let termString = termObj.termName + " " + termObj.termYear;

    if (termObj.subtermName) {
        termString = termObj.termName + " " + termObj.subtermName + " " + termObj.termYear;
    }

    return (termString);
};

module.exports = TermUtils;
